// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { type FC, memo, useState, useRef, useEffect } from 'react';
import { MessageType, DeliveryStatus } from '../../types/chat';
import type { ChatMessage as ChatMessageType } from '../../types/chat';

interface ChatMessageProps {
  message: ChatMessageType;
  isOwnMessage: boolean;
  onAddReaction: (messageId: string, emoji: string) => void;
  onRemoveReaction: (messageId: string, emoji: string) => void;
  currentPlayerId: string;
}

const COMMON_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

export const ChatMessageComponent: FC<ChatMessageProps> = memo(
  ({ message, isOwnMessage, onAddReaction, onRemoveReaction, currentPlayerId }) => {
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const messageRef = useRef<HTMLDivElement>(null);

    // Close reaction picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (messageRef.current && !messageRef.current.contains(event.target as Node)) {
          setShowReactionPicker(false);
        }
      };

      if (showReactionPicker) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
          document.removeEventListener('touchstart', handleClickOutside);
        };
      }
    }, [showReactionPicker]);

    const toggleReactionPicker = () => {
      setShowReactionPicker(prev => !prev);
    };

    const formatTime = (timestamp: string) => {
      if (!timestamp) return '--:--';
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '--:--';
      }
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const hasReacted = (emoji: string) => {
      return message.reactions?.[emoji]?.includes(currentPlayerId) || false;
    };

    const handleReactionClick = (emoji: string) => {
      if (hasReacted(emoji)) {
        onRemoveReaction(message.message_id, emoji);
      } else {
        onAddReaction(message.message_id, emoji);
      }
    };

    const isSystemMessage = message.message_type === MessageType.SYSTEM;
    const isWhisper = message.message_type === MessageType.WHISPER;

    if (isSystemMessage) {
      return (
        <div className="flex justify-center my-2">
          <div className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm px-3 py-1 rounded-full">
            {message.content}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={messageRef}
        className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} mb-3`}
        data-testid={`chat-message-${message.message_id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          {isWhisper && (
            <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
              🔒 Whisper
            </span>
          )}
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            {isWhisper && isOwnMessage
              ? `You → ${message.recipient_name || 'Unknown'}`
              : isWhisper
                ? `${message.player_name} → You`
                : message.player_name}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatTime(message.timestamp)}
          </span>
          {message.edited && (
            <span className="text-xs text-gray-400 dark:text-gray-500">(edited)</span>
          )}
          {/* Delivery status indicator (only for own messages) */}
          {isOwnMessage && message.delivery_status && (
            <span
              className="text-xs"
              title={
                message.delivery_status === DeliveryStatus.SENDING
                  ? 'Sending...'
                  : message.delivery_status === DeliveryStatus.DELIVERED
                    ? 'Delivered'
                    : 'Failed to send'
              }
            >
              {message.delivery_status === DeliveryStatus.SENDING && (
                <svg
                  className="w-3 h-3 text-gray-400 animate-pulse"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
              {message.delivery_status === DeliveryStatus.DELIVERED && (
                <svg
                  className="w-3 h-3 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              {message.delivery_status === DeliveryStatus.FAILED && (
                <svg
                  className="w-3 h-3 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </span>
          )}
        </div>

        {/* Message bubble with reaction picker */}
        <div className="relative max-w-[80%]">
          {/* Reaction picker (positioned above the message) */}
          {showReactionPicker && (
            <div
              className={`absolute bottom-full mb-2 ${isOwnMessage ? 'right-0' : 'left-0'} flex gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-1 border border-gray-200 dark:border-gray-600 z-50`}
              data-testid={`chat-reaction-picker-${message.message_id}`}
            >
              {COMMON_REACTIONS.map(emoji => (
                <button
                  key={emoji}
                  onClick={e => {
                    e.stopPropagation();
                    handleReactionClick(emoji);
                    setShowReactionPicker(false);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  data-testid={`chat-reaction-${emoji}-${message.message_id}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          <div
            onClick={toggleReactionPicker}
            onKeyDown={e => e.key === 'Enter' && toggleReactionPicker()}
            role="button"
            tabIndex={0}
            className={`px-4 py-2 rounded-2xl cursor-pointer select-none ${
              isWhisper
                ? isOwnMessage
                  ? 'bg-purple-500 text-white rounded-br-none'
                  : 'bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100 rounded-bl-none border border-purple-300 dark:border-purple-700'
                : isOwnMessage
                  ? 'bg-blue-500 text-white rounded-br-none'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-none'
            }`}
          >
            <p className="break-words whitespace-pre-wrap">{message.content}</p>
          </div>
        </div>

        {/* Reactions display */}
        {message.reactions && Object.keys(message.reactions).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(message.reactions).map(([emoji, players]) => (
              <button
                key={emoji}
                onClick={() => handleReactionClick(emoji)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm ${
                  hasReacted(emoji)
                    ? 'bg-blue-100 dark:bg-blue-900 border border-blue-300 dark:border-blue-700'
                    : 'bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600'
                } hover:scale-105 transition-transform`}
              >
                <span>{emoji}</span>
                <span className="text-xs text-gray-600 dark:text-gray-400">{players.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

ChatMessageComponent.displayName = 'ChatMessage';
